import { REACT_APP_ROUTE_HOME, REACT_APP_URL_V2_OAUTH_TOKEN } from '../../components/common/Routes/URLs';
import { BACKEND_IDS } from '../../services/ApiConfig';
import ApiServices, { getApiService } from '../../services/ApiServices';
import { getAuthToken as getAuthTokenService } from '../../services/AuthService';
import logoutService from '../../services/LogoutService';
import { history } from '../../store/store';
import { applyOrgExtraFields, persistAgency, pickOrgExtraFields } from '../../utils/helpers/normalizePortalUser';
import { startSession } from '../../utils/helpers/sessionManager';
import { clearSession, commitTokens, getAccessToken, STORAGE_KEYS, storeLoginUsername } from '../../utils/helpers/sessionStore';
import types from './types';

const isActiveSessionResponse = (responseCode) => {
  if (!responseCode) return false;

  return responseCode !== 'TE00';
};

const normalizeOrganizations = (organizations) => {
  return organizations.map((org) => {
    const sourceEntity = org.organizationEntity || {};
    const organizationEntity = {
      ...sourceEntity,
      orgId: org.orgId ?? sourceEntity.orgId,
      organizationName: org.name || org.organizationName || sourceEntity.organizationName,
      logo: org.logo ?? sourceEntity.logo
    };
    const portalUser = {
      organizationEntity,
      user: org.user || null,
      status: org.status || 'A'
    };
    applyOrgExtraFields(organizationEntity, portalUser, pickOrgExtraFields(org), pickOrgExtraFields(sourceEntity));
    if (org.portalUserConfId != null) portalUser.portalUserConfId = org.portalUserConfId;
    return portalUser;
  });
};

/**
 * Replaces any previous session with the one just issued.
 *
 * Only session keys are removed: `localStorage.clear()` used to wipe storage the
 * other tabs of this origin were still reading from.
 */
const storeSessionData = (token, refreshToken, userData, isActiveSession, responseCode) => {
  clearSession();

  commitTokens({ accessToken: token, refreshToken });

  localStorage.setItem(STORAGE_KEYS.login, JSON.stringify(userData));
  localStorage.setItem(STORAGE_KEYS.leftNavMenu, JSON.stringify([]));

  if (responseCode) {
    localStorage.setItem(STORAGE_KEYS.loginResponseCode, responseCode);
  }

  if (isActiveSession) {
    localStorage.setItem(STORAGE_KEYS.sessionActive, 'true');
  }

  startSession();
};

export const doLogin = (_url, data) => {
  return (dispatch) => {
    getAuthTokenService(REACT_APP_URL_V2_OAUTH_TOKEN, data, BACKEND_IDS.BACKEND_2)
      .then(async (res) => {
        const responseCode = res.data.code || res.data.message;
        const token = res.data.accessToken || res.data.access_token;
        const refreshToken = res.data.refreshToken || res.data.refresh_token;
        const organizations = res.data.organizations || [];
        const hasValidPayload = token && organizations && organizations.length > 0;
        const hasErrorCode = responseCode && responseCode.startsWith('TE-E');

        if (hasErrorCode && !hasValidPayload) {
          const error = {
            response: {
              status: 400,
              data: res.data
            },
            message: res.data.message || 'Login failed',
            code: res.data.code,
            errorKey: res.data.errorKey
          };
          dispatch({ type: types.LOGIN_ERROR, payload: error });
          return Promise.reject(error);
        }

        if (!hasValidPayload) {
          const error = {
            response: {
              status: 400,
              data: res.data
            },
            message: 'com.tempedge.msg.error.badcredentials',
            code: res.data.code
          };
          dispatch({ type: types.LOGIN_ERROR, payload: error });
          return Promise.reject(error);
        }

        const isActiveSession = isActiveSessionResponse(responseCode);
        const portalUserList = normalizeOrganizations(organizations);
        const userData = { portalUserList, organizations };

        storeSessionData(token, refreshToken, userData, isActiveSession, responseCode);
        storeLoginUsername(data.username);

        dispatch({ type: types.TIMEOUT_MODAL, payload: false });
        dispatch({ type: types.LOGIN, payload: userData });

        validateAgency(userData.portalUserList)();
      })
      .catch((error) => {
        const formattedError = {
          response: error.response || {
            status: error.response?.status || 400,
            data: error.response?.data || {
              code: error.code || 'TE-E00',
              message: error.message || 'Login failed',
              errorKey: error.errorKey || 'com.tempedge.msg.error.badcredentials'
            }
          },
          message: error.response?.data?.message || error.message || 'Login failed',
          code: error.response?.data?.code || error.code,
          errorKey: error.response?.data?.errorKey || error.errorKey
        };
        dispatch({ type: types.LOGIN_ERROR, payload: formattedError });
      });
  };
};

export const validateAgency = (agencyList) => {
  return () => {
    const lang = window.location.pathname.split('/')[2] || 'en';
    const destination = agencyList.length < 1 ? `/error/${lang}` : `/organization-select/${lang}`;
    history.push(destination);
  };
};

/**
 * Clears the Redux side of the session and delegates the rest to logoutService,
 * the single place that revokes the token, clears storage, tells the other tabs
 * and redirects.
 */
export const doLogout = () => {
  return async (dispatch) => {
    dispatch({ type: types.LOGOUT, payload: { access_token: getAccessToken() } });
    dispatch({ type: types.GET_CLIENT_LIST, payload: [] });
    dispatch({ type: types.CLEAR_ALL_STATE, payload: {} });

    await logoutService.logoutByUser();
  };
};

const getLanguageFromPath = () => {
  return window.location.pathname.split('/')[2] || 'en';
};

const getUserStatusRoute = (user) => {
  const lang = getLanguageFromPath();
  const status = user.status;
  const orgStatus = user.organizationEntity?.status;

  if (status === 'A' && orgStatus === 'A') {
    persistAgency(user);
    localStorage.setItem(STORAGE_KEYS.agencyValidated, Date.now().toString());
    return `/dashboard/${lang}`;
  }

  clearSession();

  if (status === 'A' && (!user.organizationEntity || orgStatus !== 'A')) {
    return `/denied/agency/${lang}`;
  }
  if (status === 'P') {
    return `/pending/user/${lang}`;
  }
  if (status === 'R') {
    return `/denied/user/${lang}`;
  }
  if (status === 'ERROR') {
    return `/error/${lang}`;
  }

  return REACT_APP_ROUTE_HOME(lang);
};

export const validateOrg = (user) => {
  const destination = getUserStatusRoute(user);
  history.push(destination);
};

export const getListClientAll = (payload) => ({
  type: types.GET_CLIENT_LIST,
  payload
});
export const getListCountryAll = (payload) => ({
  type: types.GET_COUNTRY_LIST_ALL,
  payload
});

/**
 * Uses the configured API instance (getApiService) so that 401 responses
 * trigger the response interceptor and logout/redirect to login.
 */
export const tempedgeAPI = (url, data, actionName) => {
  return async (dispatch) => {
    try {
      const apiInstance = getApiService();
      const response = await apiInstance.post(url, data);
      dispatch({
        type: actionName,
        payload: response
      });
      return response;
    } catch (error) {
      dispatch({
        type: actionName,
        payload: error
      });
      throw error;
    }
  };
};

export const clearTempedgeStoreProp = (actionProp) => {
  return (dispatch) => {
    dispatch({
      type: types.CLEAR_PROP,
      payload: actionProp
    });
  };
};

export const clearErrorField = () => {
  return (dispatch) => {
    dispatch({
      type: types.CLEAR_ERROR_FIELD,
      payload: {
        errorFields: [],
        lastRemoved: ''
      }
    });
  };
};

export const getList = (url, actionName) => {
  return async (dispatch) => {
    const response = await ApiServices.get(url);
    dispatch({
      type: actionName,
      payload: response.data.result
    });
    return response;
  };
};

export const storeFormPageNumber = (formName, position) => {
  return (dispatch) => {
    dispatch({
      type: types.SAVE_FORM_POSITION,
      payload: {
        form: formName,
        pos: position
      }
    });
  };
};

export const getFilters = (url, _data, actionName) => {
  return (dispatch) => {
    ApiServices.get(url).then((response) => {
      dispatch({
        type: actionName,
        payload: response.data.result
      });
    });
  };
};

export const saveDepartmentList = (deptList) => {
  return (dispatch) => {
    dispatch({
      type: types.SAVE_DEPARTMENTS_LIST,
      payload: deptList
    });
  };
};

export const deletePositionCLient = (index) => {
  return (dispatch) => {
    dispatch({
      type: types.DELETE_POST_CLIENT,
      payload: index
    });
  };
};

export const savePositionsList = (positionsList) => {
  return (dispatch) => {
    dispatch({
      type: types.SAVE_POSITIONS_LIST,
      payload: positionsList
    });
  };
};

export const saveToPositionsList = (newPos) => {
  return (dispatch) => {
    dispatch({
      type: types.SAVE_TO_POSITIONS_LIST,
      payload: newPos
    });
  };
};

export const removeFromDepartmentList = (index) => {
  return (dispatch) => {
    dispatch({
      type: types.REMOVE_FROM_DEPARTMENTS_LIST,
      payload: index
    });
  };
};

export const setErrorField = (fieldName) => {
  return (dispatch) => {
    dispatch({
      type: types.SET_ERROR_FIELD,
      payload: fieldName
    });
  };
};

export const removeErrorField = (fieldName) => {
  return (dispatch) => {
    dispatch({
      type: types.REMOVE_ERROR_FIELD,
      payload: fieldName
    });
  };
};

export const uploadLogo = (type, payload) => {
  return (dispatch) => {
    dispatch({
      type,
      payload
    });
  };
};

export const setCleanLogo = () => {
  return (dispatch) => {
    dispatch({
      type: types.CLEAN_LOGO
    });
  };
};

export const saveBillRates = (rate, type) => {
  return (dispatch) => {
    dispatch({
      type,
      payload: rate
    });
  };
};

export const modalVisibility = (payload) => ({
  type: types.MODAL_VISIBILITY,
  payload
});

export const pendingUser = (payload) => ({
  type: types.PENDING_USER,
  payload
});

export const optionsTableList = (id, data) => (dispatch) => {
  const datos = {
    id,
    data
  };

  return dispatch({
    type: types.ENTITY_ID,
    payload: datos
  });
};

export const dispatchOtherValue = (data, id) => (dispatch) => {
  const datos = {
    id,
    data
  };

  return dispatch({
    type: types.ENTITY_ID,
    payload: datos
  });
};

export const updateMessage = (payload) => ({
  type: types.UPDATE_MESSAGE,
  payload
});
